import { Language } from '../../models/session';

export interface LanguageOption {
    /** Backend Language enum — what we send to the API and what the chrome reads. */
    code: Language;
    /** ISO 3166-1 alpha-2 country code used by `flag-icons` to render the flag. */
    flag: string;
    /** Native name shown first so workers recognise their own language. */
    nativeName: string;
    /** English name shown in parens for the back-office. */
    englishName: string;
}

/**
 * The 20 supported chrome languages, ordered with EN first (default) and the
 * other most-common worker languages near the top. The order also matches the
 * native-language fallback shown in the dropdown.
 *
 * Country codes:
 *   EN → gb (UK English — matches Optima's market)
 *   SL → si (Slovenia's ISO country code)
 *   CS → cz (Czech Republic)
 *   Everything else: the language code IS the country code.
 */
export const LANGUAGE_OPTIONS: LanguageOption[] = [
    { code: Language.EN, flag: 'gb', nativeName: 'English',     englishName: 'English' },
    { code: Language.NL, flag: 'nl', nativeName: 'Nederlands',  englishName: 'Dutch' },
    { code: Language.PL, flag: 'pl', nativeName: 'Polski',      englishName: 'Polish' },
    { code: Language.DE, flag: 'de', nativeName: 'Deutsch',     englishName: 'German' },
    { code: Language.RO, flag: 'ro', nativeName: 'Română',      englishName: 'Romanian' },
    { code: Language.BG, flag: 'bg', nativeName: 'Български',   englishName: 'Bulgarian' },
    { code: Language.RU, flag: 'ru', nativeName: 'Русский',     englishName: 'Russian' },
    { code: Language.HU, flag: 'hu', nativeName: 'Magyar',      englishName: 'Hungarian' },
    { code: Language.FR, flag: 'fr', nativeName: 'Français',    englishName: 'French' },
    { code: Language.ES, flag: 'es', nativeName: 'Español',     englishName: 'Spanish' },
    { code: Language.IT, flag: 'it', nativeName: 'Italiano',    englishName: 'Italian' },
    { code: Language.PT, flag: 'pt', nativeName: 'Português',   englishName: 'Portuguese' },
    { code: Language.CS, flag: 'cz', nativeName: 'Čeština',     englishName: 'Czech' },
    { code: Language.SK, flag: 'sk', nativeName: 'Slovenčina',  englishName: 'Slovak' },
    { code: Language.SL, flag: 'si', nativeName: 'Slovenščina', englishName: 'Slovenian' },
    { code: Language.HR, flag: 'hr', nativeName: 'Hrvatski',    englishName: 'Croatian' },
    { code: Language.TR, flag: 'tr', nativeName: 'Türkçe',      englishName: 'Turkish' },
    { code: Language.LT, flag: 'lt', nativeName: 'Lietuvių',    englishName: 'Lithuanian' },
    { code: Language.LV, flag: 'lv', nativeName: 'Latviešu',    englishName: 'Latvian' },
    { code: Language.EE, flag: 'ee', nativeName: 'Eesti',       englishName: 'Estonian' },
];

const BY_CODE: Map<string, LanguageOption> = new Map(
    LANGUAGE_OPTIONS.map((opt) => [opt.code, opt]),
);

export const findLanguageOption = (code: string | null | undefined): LanguageOption => {
    if (!code) return LANGUAGE_OPTIONS[0];
    return BY_CODE.get(code) ?? LANGUAGE_OPTIONS[0];
};
